import React from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { addLineItems } from '../../../slices/cart';
import messages from './messages';
import PrimaryButton from '../../../components/common/PrimaryButton';
import SecondaryButton from '../../../components/common/SecondaryButton';
import styles from './AOVModalFooter.module.scss';
import { ITEM_LIST_IDS_TEALIUM } from '../../../utils/common/constants';

const AOVModalFooter = ({ lineItems }) => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const { isLoading, addLineItemsError } = useSelector((state) => ({
    addLineItemsError: state.cart.addLineItemsError,
    isLoading: state.cart.status === 'loading',
  }));

  const continueToCartHandler = () => {
    window.location.href = '/cart';
  };

  const addItemsHandler = async () => {
    const lineItemsToDispatch = Object.values(
      lineItems.reduce((acc, { sku, quantity = 1 }) => {
        const found = acc[sku];

        return {
          ...acc,
          [sku]: {
            sku,
            quantity: found ? found.quantity + 1 : quantity,
            item_list_id: ITEM_LIST_IDS_TEALIUM.minicartAovDrivers,
          },
        };
      }, {}),
    );

    const response = await dispatch(
      addLineItems({ lineItems: lineItemsToDispatch }),
    );

    if (!response.error) {
      continueToCartHandler();
    }
  };

  const hasItemsSelected = lineItems?.length > 0;

  return (
    <div className={styles['summary-container']}>
      <div className={styles['footer-button']}>
        <PrimaryButton
          isLoading={isLoading}
          onClick={addItemsHandler}
          title={formatMessage(messages.addToCart, {
            count: lineItems?.length,
          })}
          isDisabled={!hasItemsSelected}
          size='large'
        />
      </div>
      {addLineItemsError && (
        <div className={styles['summary-add-to-cart-error']}>
          {formatMessage(messages.errorAddingItems)}
        </div>
      )}
      <div className={styles['footer-button']}>
        <SecondaryButton
          onClick={continueToCartHandler}
          title={formatMessage(messages.continueToCart)}
          size='large'
        />
      </div>
    </div>
  );
};

AOVModalFooter.propTypes = {
  lineItems: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default AOVModalFooter;

const LineItem = ({
  index,
  productName,
  sku,
  isDiscountedPrice,
  regularPrice,
  discountedPrice,
}) => (
  <div className={classnames(styles['footer-line-item'])} key={`footer-${sku}-${index + 1}`}>
    {productName}
    <div
      className={classnames(
        styles['footer-line-item-prices'],
      )}
    >
      <div
        className={classnames(
          isDiscountedPrice
            ? styles['footer-line-item-reg-price']
            : null,
        )}
      >
        {regularPrice}
      </div>
      {isDiscountedPrice && (
        <div
          className={classnames(
            styles['footer-line-item-disc-price'],
          )}
        >
          {discountedPrice}
        </div>
      )}
    </div>
  </div>
);

LineItem.propTypes = {
  index: PropTypes.number.isRequired,
  productName: PropTypes.string.isRequired,
  sku: PropTypes.string.isRequired,
  isDiscountedPrice: PropTypes.number.isRequired,
  regularPrice: PropTypes.number.isRequired,
  discountedPrice: PropTypes.number.isRequired,
};